import { Box } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import CAppBreadcrumbs from '../../../app/components/CAppBreadcrumbs';
import CAppSubTitle from '../../../app/components/CAppSubTitle';
import CAppTitle from '../../../app/components/CAppTitle';
import AppCustomIcon from '../../../app/features/icon/AppIcon';
import HAppRoutes from '../../../app/features/routes/helpers/HAppRoutes';
import { useAppSettings } from '../../../app/features/settings/providers/AppSettingsProvider';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import useInvestExpertises from '../../investor/hooks/expertise/useInvestExpertises';
import useInvestFunds from '../../investor/hooks/fund/useInvestFunds';
import IFund from '../../investor/interfaces/fund/IFund';
import DocumentRoutes from '../DocumentRoutes';
import FDocumentFilters from '../features/filters/FDocumentFilters';
import HDocumentFile from '../helpers/HDocumentFile';
import useDocumentFiles from '../hooks/useDocumentFiles';
import IDocumentFile from '../interfaces/IDocumentFile';
import IDocumentsFilters from '../interfaces/IDocumentsFilters';
import langDocumentFiles from '../langs/langDocumentFiles';
import langDocumentSearch from '../langs/langDocumentSearch';
import VDocumentFilesTable from '../views/VDocumentFilesTable';

const PDocumentSearch = () => {
	const authContext = useAuthContext();
	const appSettings = useAppSettings();

	const { t } = useTranslation();

	const { documents } = useDocumentFiles();
	const { funds: allFunds } = useInvestFunds();
	const investExpertises = useInvestExpertises();

	const [filters, setFilters] = useState<IDocumentsFilters>({
		fundIds: null,
		investorIds: null,
		documentType: null,
		releasePeriod: null,
		format: null,
		fundType: null,
		fundVintage: null,
	});

	// Par défaut on sélectionne tous les fonds
	useEffect(() => {
		if (appSettings && (!filters.fundIds || filters.fundIds.length === 0) && allFunds && allFunds.length > 0) {
			setFilters({
				...filters,
				fundIds: allFunds
					.filter(
						(x) =>
							appSettings.realizationStatus === 'all' ||
							(x.isRealized === true && appSettings.realizationStatus === 'realized') ||
							(x.isRealized === false && appSettings.realizationStatus === 'unrealized'),
					)
					.map((x: IFund) => x.id),
			});
		}
	}, [filters, allFunds, appSettings]);

	const [fundsFiltered, setFundsFiltered] = useState<IFund[] | null>(null);
	useEffect(() => {
		if (allFunds) {
			let fundsTmp = _.cloneDeep(allFunds);
			if (filters) {
				if (filters.fundIds) {
					fundsTmp = fundsTmp.filter((x) => (filters.fundIds ?? []).includes(x.id));
				}
				if (filters.investorIds) {
					fundsTmp = fundsTmp.filter((x) =>
						x?.investors?.some((i) => filters?.investorIds?.includes(i.company.id)),
					);
				}
				if (filters.fundType) {
					fundsTmp = fundsTmp.filter((x) => x.typeTag === filters.fundType);
				}
				if (filters.fundVintage) {
					fundsTmp = fundsTmp.filter((x) => x.vintage === filters.fundVintage);
				}
			}
			setFundsFiltered(fundsTmp);
		} else {
			setFundsFiltered(null);
		}
	}, [allFunds, filters]);

	const [documentsFiltered, setDocumentsFiltered] = useState<IDocumentFile[] | null>(null);
	useEffect(() => {
		if (fundsFiltered && documents && investExpertises) {
			let documentsTmp = _.cloneDeep(documents);

			if (fundsFiltered) {
				documentsTmp = documentsTmp.filter((x) => fundsFiltered.map((f) => f.id).includes(x.fund?.id ?? -1));
			}
			if (filters) {
				if (filters.keywords) {
					const searchWords = filters.keywords.split(' ');
					searchWords.forEach((word) => {
						documentsTmp = documentsTmp.filter((x) =>
							HDocumentFile.cleanWord(x.name).includes(HDocumentFile.cleanWord(word)),
						);
					});
				}
				if (filters.investorIds) {
					documentsTmp = documentsTmp.filter(
						(x) => x.company === null || (filters.investorIds ?? []).includes(x.company?.id ?? -1),
					);
				}
				if (filters.documentType) {
					documentsTmp = documentsTmp.filter((x) => x.documentType === filters.documentType);
				}
				if (filters.format) {
					documentsTmp = documentsTmp.filter((x) => x.formatTag === filters.format);
				}
				if (filters.releasePeriod) {
					switch (filters.releasePeriod) {
						case '1_month':
							documentsTmp = documentsTmp.filter(
								(x) => moment(x.date).diff(moment().subtract(1, 'month'), 'days') > 0,
							);
							break;
						case '3_months':
							documentsTmp = documentsTmp.filter(
								(x) => moment(x.date).diff(moment().subtract(3, 'month'), 'days') > 0,
							);
							break;
						case '6_months':
							documentsTmp = documentsTmp.filter(
								(x) => moment(x.date).diff(moment().subtract(6, 'month'), 'days') > 0,
							);
							break;
						case '1_year':
							documentsTmp = documentsTmp.filter(
								(x) => moment(x.date).diff(moment().subtract(1, 'year'), 'days') > 0,
							);
							break;
						default:
							null;
					}
				}
			}
			setDocumentsFiltered(
				_.orderBy(documentsTmp, ['isNew', 'fund.isRealized', 'date'], ['desc', 'asc', 'desc']),
			);
		}
	}, [documents, fundsFiltered, filters, investExpertises]);

	return (
		<>
			{documentsFiltered ? (
				<>
					<Helmet>
						<title>
							{authContext.authContext === 'employee' ? '[E] ' : ''} {t(langDocumentFiles.title.tag)} -{' '}
							{t(langDocumentSearch.title.tag)} -{' '}
							{authContext.currentCompany ? authContext.currentCompany.name : ''}
						</title>
					</Helmet>
					<CAppBreadcrumbs
						items={[
							{
								label: t(langDocumentFiles.title.tag),
								location: HAppRoutes.getPath(DocumentRoutes.dashboard.tag),
							},

							{
								label: t(langDocumentSearch.title.tag),
							},
						]}
					/>
					<CAppTitle title={t(langDocumentSearch.title.tag)} />
					<Box sx={{ marginTop: '30px' }}>
						<FDocumentFilters
							orderFilters={[
								{
									filterTag: 'keywords',
									xs: 12,
									md: 4,
								},

								{
									filterTag: 'documentType',
									xs: 12,
									md: 4,
								},
								{
									filterTag: 'releasePeriod',
									xs: 12,
									md: 4,
								},
								{
									filterTag: 'fundType',
									xs: 12,
									md: 4,
								},
								{
									filterTag: 'fundIds',
									xs: 12,
									md: 4,
								},
								{
									filterTag: 'investorIds',
									xs: 12,
									md: 4,
								},
							]}
							allDocuments={documentsFiltered}
							allFunds={allFunds ?? undefined}
							filters={filters}
							onChange={(newFilters) => setFilters(newFilters)}
						/>
					</Box>

					<CAppSubTitle
						title={t(langDocumentFiles.x_documents.tag, { count: documentsFiltered.length })}
						icon={<AppCustomIcon tag="folders" />}
					/>
					<Box component="span" style={{ display: 'none' }} id="nb-documents-filtered">
						{documentsFiltered.length}
					</Box>
					<VDocumentFilesTable documents={documentsFiltered} />
				</>
			) : (
				<></>
			)}
		</>
	);
};

export default PDocumentSearch;
